import ExpandableCaption from 'components/common/ExpandableCaption';
import RiveBadge from 'components/common/RiveBadge';
import { ReportIcon } from 'components/common/svgIcons';
import UserButton from 'components/community/shared/UserButton';
import DeleteShotButton from 'components/community/shot/DeleteShotButton';
import ConfirmDeleteCommunityPost from 'components/profile/ConfirmDeleteCommunityPost';
import { UserContext } from 'contexts/user';
import * as analytics from 'lib/analytics';
import { communityReport } from 'lib/analytics';
import { getShareLink } from 'lib/community';
import { generateMailTo } from 'lib/mailto';
import sanitizeHTML from 'lib/sanitizeHTML';
import { jsonFetcherWithHandler as fetcher } from 'lib/swr';
import React, { useContext, useState } from 'react';
import useSWR from 'swr';
import { VoidCallback } from 'types/callbacks';
import { CommunityPost, CommunityShotItem } from 'types/community';
import RemixModal from '../remix/RemixModal';
import CommentsButton from './CommentsButton';
import MetadataLabel from './MetadataLabel';
import OpenInRiveCommunityButton from './OpenInRiveCommunityButton';
import ParentPosts from './ParentPosts';
import ReactionButton from './ReactionButton';
import RemixButton from './RemixButton';
import RevisionSelect from './RevisionSelect';
import styles from './ShotMetadata.module.css';
import TagLinks from './TagLinks';
import featureFlags from 'lib/featureFlags';

type ShotMetadataProps = {
  shotItem: CommunityShotItem;
  children?: React.ReactNode;
  disableReactionsTray: boolean;
  onCommentsClick: VoidCallback;
};

function shotItemHandler(data: any) {
  return CommunityPost.fromAPI(data).toCommunityShotItem();
}

export default function ShotMetadata({
  shotItem,
  children,
  disableReactionsTray,
  onCommentsClick,
}: ShotMetadataProps) {
  const [isDeleteModelOpen, setIsDeleteModelOpen] = useState<boolean>(false);
  const [isRemixModalOpen, setIsRemixModalOpen] = useState<boolean>(false);

  const { data: fetchedShotItem } = useSWR(
    `/api/community-posts/${shotItem.communityPostId}`,
    fetcher(shotItemHandler),
    { fallbackData: shotItem }
  );

  const { user } = useContext(UserContext);

  const revisions = fetchedShotItem!.revisions;

  const description = sanitizeHTML(shotItem.description);

  const mailToString = generateMailTo({
    to: 'support@rive.app',
    subject: `Community Content Report - ${shotItem.urlSlug}`,
    body: `I am reporting content found on the Rive Community.

URL: ${getShareLink(shotItem)}
Reason:`,
  });

  const { communityPostId, slug, title, fileId } = shotItem;

  function onReportClick() {
    communityReport(communityPostId, slug, title, fileId);
  }

  function onRemixClick() {
    if (user?.signedIn) {
      setIsRemixModalOpen(true);
      return;
    }

    const loginUrl = `/login?redirect=${encodeURIComponent(
      window.location.pathname
    )}`;
    // If not signed in, redirect to the login page
    window.location.assign(loginUrl);
    analytics.clickCommunityRemix(
      shotItem.communityPostId,
      shotItem.fileId,
      shotItem.title,
      shotItem.isRemix,
      'redirect_to_login'
    );
  }

  return (
    <>
      <ConfirmDeleteCommunityPost
        display={isDeleteModelOpen}
        onCancel={() => setIsDeleteModelOpen(false)}
        shotItem={shotItem}
      />
      <RemixModal
        display={isRemixModalOpen}
        onClose={() => setIsRemixModalOpen(false)}
        shotItem={shotItem}
      />
      <div className={styles.ShotMetadataColumn}>
        <div className={styles.ShotMetadata}>
          <div className={styles.MetaContainer}>
            <div
              className={
                featureFlags.dataBinding() && shotItem.isAnimationStateMachine
                  ? styles.CoreMetdataLeva
                  : styles.CoreMetdata
              }
            >
              <div className={styles.Header}>
                <div className={styles.TitleContainer}>
                  <h3>{shotItem.title}</h3>
                  {shotItem.isRemix && (
                    <div className={styles.RemixLabel}>
                      <RiveBadge>REMIX</RiveBadge>
                    </div>
                  )}
                </div>

                <a
                  className={styles.ReportAction}
                  href={mailToString}
                  onClick={onReportClick}
                >
                  <ReportIcon width={10} />
                </a>
              </div>
              <UserButton className={styles.User} data={shotItem} />
              <ExpandableCaption>
                <TagLinks className={styles.TagLinks}>{description}</TagLinks>
              </ExpandableCaption>
              <div className={styles.MetadataLabels}>
                <MetadataLabel label="License">
                  <a href="https://creativecommons.org/licenses/by/4.0/">
                    CC BY
                  </a>
                </MetadataLabel>
                <MetadataLabel label="Viewing">
                  <RevisionSelect
                    lastUpdated={shotItem.lastUpdated}
                    currentRevisionId={shotItem.communityRevisionId}
                    revisions={revisions}
                  />
                </MetadataLabel>
              </div>
            </div>
            {shotItem.parent && (
              <ParentPosts parent={shotItem.parent} root={shotItem.root} />
            )}
          </div>
          {children}
        </div>
        <div className={styles.ShotMetadata}>
          <div className={styles.ActionsList}>
            <ReactionButton
              shotItem={shotItem}
              disableReactionsTray={disableReactionsTray}
            />
            <CommentsButton shotItem={shotItem} onClick={onCommentsClick} />
          </div>
        </div>
        <div className={styles.ShotMetadata}>
          <div className={styles.ActionsList}>
            {<RemixButton shotItem={shotItem} onClick={onRemixClick} />}
            <OpenInRiveCommunityButton shotItem={shotItem} />
          </div>
        </div>
        {user?.ownerId === shotItem.ownerId && (
          <div className={styles.ShotMetadata}>
            <div className={styles.ActionsList}>
              <DeleteShotButton onClick={() => setIsDeleteModelOpen(true)} />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

ShotMetadata.defaultProps = {
  disableReactionsTray: false,
};
